
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import Chart from 'react-apexcharts';

import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Coins, Factory, TrendingDown, TriangleAlert, Wallet } from "lucide-react";

import KpiTiles, { type KpiTone } from "@/components/KpiTiles";
import { Decarbonisation, DecarbMeasure, fetchDecarbonisation } from "../../PVPotentialAPI";

class Properties {
    object: any;
}

const currencyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const nf = (v: number | null | undefined, digits = 0) =>
    v == null ? '—' : new Intl.NumberFormat('de-DE', { maximumFractionDigits: digits }).format(v);

/** Urteil der Maßnahme als Plakette: grün lohnt sich, gedeckt bei schwacher Wirkung oder fehlender Wirtschaftlichkeit. */
const VERDICT_STYLE: Record<string, React.CSSProperties> = {
    'lohnt sich': { background: 'color-mix(in srgb, #1a9850 14%, transparent)', color: '#14773f' },
    'lohnt sich nicht': { background: 'color-mix(in srgb, #e08a00 16%, transparent)', color: '#9a5f00' },
    'wenig Wirkung': { background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' },
};

const START_YEARS = Array.from({ length: 16 }, (_, i) => 2025 + i);

/**
 * Dekarbonisierungspfad des Gebäudes gegen den Zielpfad nach Klimaschutzgesetz (tri-server `decarbonisation.ts`):
 * Ausgangswert gemessen, hochgerechnet oder geschätzt, Maßnahmen aus dem Gebäude mit wählbarem Startjahr.
 * Ersetzt die feste Kurve (33 kg, erfundene Zielkurve, gleiche Maßnahmen für jedes Gebäude).
 */
export default function DecarbonizationProperties(properties: Properties) {
    const buildingId = properties.object?._id ? String(properties.object._id) : properties.object?.id;
    const [data, setData] = useState<Decarbonisation | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [chosen, setChosen] = useState<Partial<Record<DecarbMeasure['key'], number>>>({});

    useEffect(() => {
        setData(null);
        setError(null);
        setChosen({});
    }, [buildingId]);

    useEffect(() => {
        if (!buildingId) return;
        let cancelled = false;
        fetchDecarbonisation(buildingId, chosen)
            .then(r => { if (!cancelled) { setData(r); setError(null); } })
            .catch(e => { if (!cancelled) setError(e?.message ?? String(e)); });
        return () => { cancelled = true; };
    }, [buildingId, chosen]);

    const chart = useMemo(() => {
        if (!data?.available || !data.path) return null;
        const years = data.path.map(p => p.year);
        const series = [
            { name: 'Zielpfad (KSG)', data: data.path.map(p => p.target) },
            { name: 'Ohne Maßnahmen', data: data.path.map(p => p.baseline) },
            ...(data.chosen?.length ? [{ name: 'Mit Maßnahmen', data: data.path.map(p => p.withMeasures) }] : []),
        ];
        const point = (year: number | null | undefined, value: number | undefined, text: string, color: string) =>
            year && value != null ? [{ x: year, y: value, marker: { size: 6, fillColor: color, strokeColor: color }, label: { text, borderColor: color, style: { background: color, color: '#fff' } } }] : [];
        const at = (year: number | null | undefined, key: 'baseline' | 'withMeasures') => data.path!.find(p => p.year === year)?.[key];
        return {
            series,
            options: {
                chart: { type: 'line' as const, toolbar: { show: false }, zoom: { enabled: false } },
                colors: ['#576071', '#d7191c', '#1a9850'],
                stroke: { width: [2, 2, 3], dashArray: [6, 0, 0] },
                dataLabels: { enabled: false },
                xaxis: { categories: years, type: 'numeric' as const, tickAmount: 5, labels: { formatter: (v: string) => String(Math.round(Number(v))) } },
                yaxis: { min: 0, title: { text: 'kg CO₂/m² im Jahr' }, labels: { formatter: (v: number) => nf(v, 0) } },
                tooltip: { y: { formatter: (v: number) => `${nf(v, 1)} kg CO₂/m²` } },
                legend: { position: 'top' as const },
                annotations: {
                    points: [
                        ...point(data.stranding?.baseline, at(data.stranding?.baseline, 'baseline'), `Über dem Pfad ab ${data.stranding?.baseline}`, '#d7191c'),
                        ...(data.chosen?.length ? point(data.onTrackFrom?.withMeasures, at(data.onTrackFrom?.withMeasures, 'withMeasures'), `Auf Pfad ab ${data.onTrackFrom?.withMeasures}`, '#1a9850') : []),
                    ],
                },
            },
        };
    }, [data]);

    // Auch Flurstück und Grundstück zeigen den Reiter; der Pfad gehört aber zu einem Gebäude
    if (error && /^404\b/.test(error)) return <p className="text-sm text-muted-foreground">Den Dekarbonisierungspfad gibt es je Gebäude: im Gebäudepanel unter Dekarbonisierung.</p>;
    if (error) return <p className="text-sm text-destructive">Der Pfad konnte nicht berechnet werden: {error}</p>;
    if (!data) return <p className="text-sm text-muted-foreground">Berechne Dekarbonisierungspfad …</p>;
    if (!data.available) return <p className="text-sm text-muted-foreground">{data.reason}.</p>;

    const toggle = (key: DecarbMeasure['key']) =>
        setChosen(c => {
            const next = { ...c };
            if (next[key] !== undefined) delete next[key];
            else next[key] = 2027;
            return next;
        });
    const any = (data.chosen?.length ?? 0) > 0;
    const b = data.baseline!;
    const today = data.path?.[0];

    return <div className="flex min-w-0 max-w-[1400px] flex-col gap-4">
        <KpiTiles items={[
            {
                label: 'Heute', value: nf(today?.baseline, 1), unit: 'kg CO₂/m²', hint: `Zielpfad ${nf(today?.target, 1)} kg/m²`,
                icon: Factory, tone: today && today.baseline > today.target ? 'bad' : 'good', emphasis: true,
            },
            {
                label: 'Über dem Zielpfad', value: data.stranding?.baseline ? String(data.stranding.baseline) : 'nie', hint: data.stranding?.baseline ? 'ohne Maßnahmen' : 'bis 2044, ohne Maßnahmen',
                icon: TriangleAlert, tone: data.stranding?.baseline ? (data.stranding.baseline <= 2030 ? 'bad' : 'warn') : 'good',
            },
            ...(any ? [{
                label: 'Mit Maßnahmen', value: data.onTrackFrom?.withMeasures ? `ab ${data.onTrackFrom.withMeasures}` : 'nicht', unit: data.onTrackFrom?.withMeasures ? 'auf Pfad' : 'auf Pfad',
                hint: `Rest 2045: ${nf(data.residual2045?.withMeasures, 1)} kg/m²`, icon: TrendingDown, tone: (data.onTrackFrom?.withMeasures ? 'good' : 'warn') as KpiTone,
            }] : []),
            {
                label: 'CO₂-Kosten bis 2045', value: currencyFormat.format(any ? data.co2Cost2045!.withMeasures : data.co2Cost2045!.baseline),
                hint: any ? `ohne Maßnahmen ${currencyFormat.format(data.co2Cost2045!.baseline)}` : 'ohne Maßnahmen', icon: Coins, tone: 'warn',
            },
            ...(any ? [{ label: 'Investition', value: currencyFormat.format(data.investment ?? 0), hint: 'nach Förderung', icon: Wallet, tone: 'primary' as KpiTone }] : []),
        ]} />
        <p className="text-sm">
            Ausgangswert <span className="font-bold">{b.stage}</span>: {b.source}; {nf(b.areaM2)} m², {nf(b.electricityKWh)} kWh Strom
            und {nf(b.fuelKWh)} kWh {b.fuelLabel} im Jahr.
        </p>
        {chart && <Chart options={chart.options as any} series={chart.series} type="line" height="300" />}

        <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">Maßnahmen</h3>
        {data.measures?.length ? <div className="overflow-x-auto rounded-md border">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>Maßnahme</TableHead>
                        <TableHead className="w-[120px]">ab</TableHead>
                        <TableHead className="text-right">Investition</TableHead>
                        <TableHead className="text-right">Förderung</TableHead>
                        <TableHead className="text-right">Ersparnis/Jahr</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data.measures.map(m => (
                        <TableRow key={m.key}>
                            <TableCell>
                                <Checkbox aria-label={m.label} checked={chosen[m.key] !== undefined} onCheckedChange={() => toggle(m.key)} />
                            </TableCell>
                            <TableCell className="min-w-0 break-words">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={m.assessment && m.assessment.verdict !== 'lohnt sich' ? 'text-muted-foreground' : ''}>{m.label}</span>
                                    {m.assessment && <span
                                        className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                                        style={VERDICT_STYLE[m.assessment.verdict]}
                                        title={m.assessment.abatementCostPerT != null ? `CO₂-Vermeidungskosten ${nf(m.assessment.abatementCostPerT)} €/t über ${m.assessment.lifetimeYears} Jahre` : undefined}
                                    >{m.assessment.verdict}</span>}
                                </div>
                                <div className="text-xs text-muted-foreground">{m.detail}{m.assessment ? ` · ${m.assessment.reason}` : ''}</div>
                            </TableCell>
                            <TableCell>
                                <Select value={String(chosen[m.key] ?? 2027)} disabled={chosen[m.key] === undefined}
                                    onValueChange={v => setChosen(c => ({ ...c, [m.key]: Number(v) }))}>
                                    <SelectTrigger aria-label={`Startjahr ${m.label}`} className="w-[100px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>{START_YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                                </Select>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{currencyFormat.format(m.investment)}</TableCell>
                            <TableCell className="text-right tabular-nums">{m.subsidy ? currencyFormat.format(m.subsidy) : '—'}</TableCell>
                            <TableCell className="text-right tabular-nums">{currencyFormat.format(m.savingsPerYear)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div> : <p className="text-sm text-muted-foreground">Für dieses Gebäude lassen sich keine Maßnahmen rechnen (kein 3D-Modell).</p>}

        <div className="text-xs text-muted-foreground">
            <p>{data.reference?.note}. {data.caveat}</p>
            {data.sources?.map(s => <p key={s}>{s}</p>)}
        </div>
    </div>;
}
